<template>
  <component
    :is="element"
    ref="btnMain"
    class="button"
    :class="[
      $blockClass(blockClass, 'button'),
      modifierClasses,
      { [$class('active')]: showStateIcon },
      { [$class('disabled')]: disabled }
    ]"
    :disabled="disabled"
    :href="url || null"
    :type="type || null"
    @mouseenter="handleHover"
    @mouseleave="handleHover"
    @focusin="handleHover"
    @focusout="handleHover"
  >
    <span
      class="button__label"
      :class="[
        $blockClass(blockClass, 'button-label'),
        { 'button__label--no-icon': !showIcon },
        classLabel,
      ]"
      v-text="label"
    />

    <span
      v-if="showIcon"
      class="button__icon"
      :class="$blockClass(blockClass, 'button-icon')"
    >
      <animated-icon
        ref="buttonIcon"
        transition-type="swipe-right"
        :transition-on-hover="false"
        :focuasable="false"
      >
        <template #iconOne>
          <slot name="animatedIconOne">
            <icon-arrow-right />
          </slot>
        </template>

        <template #iconTwo>
          <slot name="animatedIconTwo">
            <icon-arrow-right />
          </slot>
        </template>
      </animated-icon>

    </span>

    <span
      v-if="showStateIcon"
      class="button__state"
      :class="$blockClass(blockClass, 'button-state')"
    >
      <slot name="stateIcon" />
    </span>
  </component>
</template>

<script>

/**
 * Vue: Button (btn)
 * -----------------------------------------------------------------------------
 * Global interactive button element.
 * - Styles are imported in critical styles.
 * - Must be imported as <btn> as <button> is a default element.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {String} [classLabel] - Class to apply to the button label element.
 * @param {String} [customElement] - Optional override to set button element.
 * @param {Boolean} isDisabled - Applies the disabled state to the button.
 * @param {String} label - Button label.
 * @param {String} [modifiers] - Button modifiers in space separated list
  (secondary, light, outlined, text, block, reversed, or large).
 * @param {Boolean} [showIcon] - Whether to show the button icon.
 * @param {Boolean} [showStateIcon] - Show secondary button state, replaces text
 * and default icon with `stateIcon` slot.
 * @param {String, Boolean} state - Buttons current state.
 * @param {String} [type] - Type of button (button or submit).
 * @param {String} [url] - URL if link.
 *
 */
import IconArrowRight from '~icons/direction/arrow/right.svg'

import AnimatedIcon from '~global/animated-icon/animated-icon.vue'

export default {
  name: 'Btn',

  components: {
    IconArrowRight,
    AnimatedIcon,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    classLabel: {
      type: String,
      default: '',
    },
    customElement: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
      required: true,
    },
    modifiers: {
      type: [String, Array],
      default: '',
    },
    showIcon: {
      type: Boolean,
      default: true,
    },
    showStateIcon: {
      type: Boolean,
      default: false,
    },
    state: {
      type: [String, Boolean],
      default: false,
    },
    type: {
      type: String,
      default: '',
    },
    url: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      global: true,
      freezeIcons: false,
    }
  },

  computed: {

    /**
     * Compute element type.
     * @returns {String}
     */
    element() {
      if (this.customElement) {
        return this.customElement
      }

      return this.url ? 'a' : 'button'
    },

    /**
     * Compute modifier classes.
     * @returns {String}
     */
    modifierClasses() {
      if (!this.modifiers) {
        return ''
      }

      let modifiers = []

      if (Array.isArray(this.modifiers)) {
        modifiers = this.modifiers
      } else {
        modifiers = this.modifiers.split(/[\s,]/g)
      }

      return modifiers.map((modifier) => {
        return `button--${modifier}`
      }).join(' ')
    },
  },

  watch: {
    state(val) {
      if (!val) {
        return
      }

      this.$refs.btnMain.blur()

      if (val === 'added') {
        this.$refs.buttonIcon.resetIcons('two')
        return
      }

      this.$refs.buttonIcon.resetIcons('one')
    },
  },

  methods: {

    /**
     * Responsible for firing the function on the child element on interaction.
     * @param {Object} val - Pointer event.
     */
    handleHover(val) {
      if (this.state && this.state === 'added') {
        return
      }

      if (!this.showIcon || this.disabled) {
        return
      }

      if (val.type === 'mouseenter' || val.type === 'focusenter') {
         this.$refs.buttonIcon.resetIcons('two')
      }

      if (val.type === 'mouseleave' || val.type === 'focusleave') {
        this.$refs.buttonIcon.resetIcons('one')
      }
    },
  },
}
</script>
